import type { DataProvider } from '@refinedev/core';
import { api } from './api-client';

/**
 * Refine DataProvider customizado pra automation-api.
 *
 * O backend retorna shape `{ data, count, limit, offset }` em listas e
 * `{ data: { ... } }` em singles. Mappeamos pro contrato Refine.
 *
 * Resources mapeados (path absoluto começa em `/`, sem `/api` — esse já
 * está no API_BASE do api-client):
 *   psn-accounts  → /psn-accounts
 *   topup         → /topup
 *   audit-log     → /audit-log
 *
 * Recovery codes não são resource Refine (são sub-recurso). Use useCustom
 * direto contra `/psn-accounts/:accountId/recovery-codes/...`.
 */

const RESOURCES: Record<string, string> = {
  'psn-accounts': '/psn-accounts',
  topup: '/topup',
  'audit-log': '/audit-log',
};

function resolveResource(resource: string): string {
  return RESOURCES[resource] ?? `/${resource}`;
}

export const dataProvider: DataProvider = {
  getApiUrl: () => '',

  getList: async ({ resource, pagination, filters }) => {
    const path = resolveResource(resource);
    const query: Record<string, string | number> = {};

    if (pagination?.mode !== 'off') {
      const current = pagination?.current ?? 1;
      const pageSize = pagination?.pageSize ?? 50;
      query.limit = pageSize;
      query.offset = (current - 1) * pageSize;
    }

    if (filters) {
      for (const f of filters) {
        if ('field' in f && f.value !== undefined && f.value !== null && f.value !== '') {
          query[f.field] = String(f.value);
        }
      }
    }

    const res = await api<{ data: unknown[]; count: number }>(path, { query });
    return {
      data: res.data as never[],
      total: res.count,
    };
  },

  getOne: async ({ resource, id }) => {
    const path = resolveResource(resource);
    const res = await api<{ data: unknown }>(`${path}/${id}`);
    return { data: res.data as never };
  },

  create: async ({ resource, variables }) => {
    const path = resolveResource(resource);
    const res = await api<{ data: unknown }>(path, {
      method: 'POST',
      body: variables,
    });
    return { data: res.data as never };
  },

  update: async ({ resource, id, variables }) => {
    const path = resolveResource(resource);
    const res = await api<{ data: unknown }>(`${path}/${id}`, {
      method: 'PUT',
      body: variables,
    });
    return { data: res.data as never };
  },

  deleteOne: async () => {
    // API não suporta DELETE direto — todos os "deletes" são state transitions
    // (status='retired' via PUT) ou cascade do banco. Refine espera o método
    // mas não vamos usar até a UI precisar.
    throw new Error('DELETE not supported — use status transitions');
  },

  custom: async ({ url, method, payload, query, headers }) => {
    const res = await api<unknown>(url, {
      method: (method ?? 'GET').toUpperCase() as RequestInit['method'],
      body: payload,
      query: query as Record<string, string | number | boolean | undefined> | undefined,
      headers: headers as HeadersInit | undefined,
    });
    return { data: res as never };
  },
};
